import { CheckIcon, ChevronRightIcon, ChevronUpIcon, XIcon } from "@primer/octicons-react";
import { Box, Checkbox, IconButton } from "@primer/react";
import NumberBox from "devextreme-react/number-box";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hocSinhApi } from "../../../api/hocSinhApi";
import { mucThuPhiApi } from "../../../api/mucThuPhiApi";
import { saleSetApi } from "../../../api/saleSetApi";
import { showNotify } from "../../../helpers/toast";
import DataGrid from "../../../components/data-grid";
import { IDataGridColumn } from "../../../components/data-grid/type/IDataGridProps";
import Button from "../../../components/ui/button";
import ModalActions from "../../../components/ui/modal/ModalActions";
import TextWithEllipse from "../../../components/ui/text-with-ellipse";
import { RootState } from "../../../state/reducers";
import { DefaultPopup } from "../../modal";
import { loadKhoanNopStart } from "../../../state/actions/khoanNopActions";
import { PageBaseStatus } from "../../../models/pageBaseStatus";

type ThemHeClbKhacModalProps = {
  nam_hoc: string;
  ts_hocsinh_id: number;
  onAdd: (items: any[]) => void;
  onAddAndSubmit?: (items: any[]) => void;
  onClose: () => void;
  isSaveToSaleset?: boolean;
};

export const ThemHeClbKhacModal = (props: ThemHeClbKhacModalProps) => {
  const state = useSelector((state: RootState) => state.khoanNop);

  const { dm_coso_id } = useSelector((s: RootState) => s.common);
  const [datasource, setDatasource] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const {
    status,
    sf_khoannops,
  } = state;

  useEffect(() => {
    if (state.status === PageBaseStatus.is_need_reload) {
      // dispatch(loadKhoanNopStart());
    }
  }, [state]);
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const hsRes = await hocSinhApi.selectByIdNamHoc({ ts_hocsinh_id: props.ts_hocsinh_id, nam_hoc: props.nam_hoc });
        const hs = hsRes.is_success ? hsRes.data : null;

        // Lấy sf_saleset để loại bỏ tất cả khoản đã có trong saleset
        // const salesetRes = await saleSetApi.selectByStudent({
        //   nam_hoc: props.nam_hoc,
        //   ts_hocsinh_id: props.ts_hocsinh_id,
        // });
        // const existingSalesetKhoanNopIds = new Set<number>();
        // if (salesetRes.is_success && Array.isArray(salesetRes.data)) {
        //   salesetRes.data.forEach((x: any) => {
        //     existingSalesetKhoanNopIds.add(x.sf_khoannop_id ?? x.id);
        //   });
        // }

        const res = await mucThuPhiApi.select_by_nam_nhap_hoc({
          dm_truong_id: hs?.dm_truong_id ?? 0,
          dm_khoi_id: hs?.dm_khoi_id ?? 0,
          dm_he_id: hs?.dm_he_id ?? 0,
          nam_hoc: props.nam_hoc,
          nam_nhap_hoc: hs?.nam_nhap_hoc ?? props.nam_hoc,
        });
        if (res.is_success && Array.isArray(res.data)) {
          // Lấy tất cả khoản nộp: bỏ khoản đã có trong saleset + bỏ khoản dịch vụ
          const items = res.data
            // .filter((x: any) => !existingSalesetKhoanNopIds.has(x.sf_khoannop_id ?? x.id))
            .filter((x: any) => !x.is_tinhcongno && (x.sf_nhomkhoannop_id === 8 || x.sf_nhomkhoannop_id === 10))
            .map((x: any) => ({
              ...x,
              id: x.sf_khoannop_id ?? x.id,
              so_tien_phai_nop: x.so_tien || 0,
              ghi_chu: "",
            }));
          setDatasource([...items]);
        }
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [props.nam_hoc, props.ts_hocsinh_id, dm_coso_id]);


  // Grouped + sorted data_source
  const data_source = useMemo(() => {
    const getOrder = (item: any) => ({
      thuTuNhom: Number(item.thutu_sapxep_nhomkhoannop) || 0,
      thuTu: Number(item.thutu_sapxep) || 0,
    });

    const groupedData: Record<string, any[]> = {};
    datasource.forEach((item: any) => {
      const groupKey = item.nhom_khoan_nop || "Khác";
      if (!groupedData[groupKey]) groupedData[groupKey] = [];
      groupedData[groupKey].push(item);
    });

    const result: any[] = [];
    const sortedGroupKeys = Object.keys(groupedData).sort((a, b) => {
      const ordersA = groupedData[a].map(getOrder);
      const ordersB = groupedData[b].map(getOrder);
      const minNhomA = Math.min(...ordersA.map((o) => o.thuTuNhom));
      const minNhomB = Math.min(...ordersB.map((o) => o.thuTuNhom));
      if (minNhomA !== minNhomB) return minNhomA - minNhomB;
      const minThuTuA = Math.min(...ordersA.map((o) => o.thuTu));
      const minThuTuB = Math.min(...ordersB.map((o) => o.thuTu));
      if (minThuTuA !== minThuTuB) return minThuTuA - minThuTuB;
      return String(a).localeCompare(String(b), "vi");
    });

    sortedGroupKeys.forEach((groupKey) => {
      result.push({
        id: `group_${groupKey}`,
        isGroupHeader: true,
        nhom_khoan_nop: groupKey,
        ma_khoan_nop: "",
        ten_khoan_nop: `Nhóm: ${groupKey}`,
        groupItems: groupedData[groupKey],
        isExpanded: expandedGroups.has(groupKey),
      });
      if (expandedGroups.has(groupKey)) {
        const sortedItems = [...groupedData[groupKey]].sort((x: any, y: any) => {
          const a = getOrder(x);
          const b = getOrder(y);
          if (a.thuTu !== b.thuTu) return a.thuTu - b.thuTu;
          return String(x.ma_khoan_nop || "").localeCompare(String(y.ma_khoan_nop || ""), "vi");
        });
        sortedItems.forEach((item) => result.push({ ...item, isGroupHeader: false, parentGroup: groupKey }));
      }
    });
    return result;
  }, [datasource, expandedGroups]);

  // Auto-expand all groups khi data load
  const uniqueGroupsStr = useMemo(() => {
    const uniqueGroups = new Set<string>();
    datasource.forEach((item) => uniqueGroups.add(item.nhom_khoan_nop || "Khác"));
    return Array.from(uniqueGroups).sort().join(",");
  }, [datasource]);

  useEffect(() => {
    if (!uniqueGroupsStr) return;
    setExpandedGroups((prev) => {
      const currentGroups = Array.from(prev.keys()).sort().join(",");
      if (currentGroups !== uniqueGroupsStr) {
        const newMap = new Map<string, string>();
        uniqueGroupsStr.split(",").forEach((k) => newMap.set(k, k));
        return newMap;
      }
      return prev;
    });
  }, [uniqueGroupsStr]);

  const toggleGroup = useCallback((groupKey: string) => {
    setExpandedGroups((prev) => {
      const newMap = new Map(prev);
      if (newMap.has(groupKey)) newMap.delete(groupKey);
      else newMap.set(groupKey, groupKey);
      return newMap;
    });
  }, []);

  const handleGroupSelection = useCallback((groupKey: string, isSelected: boolean) => {
    const groupIds = datasource.filter((x) => (x.nhom_khoan_nop || "Khác") === groupKey).map((x) => x.id);
    setSelectedIds((prev) => {
      if (isSelected) {
        const next = [...prev];
        groupIds.forEach((id) => { if (!next.includes(id)) next.push(id); });
        return next;
      }
      return prev.filter((id) => !groupIds.includes(id));
    });
  }, [datasource]);

  const handleItemSelection = useCallback((id: number, isSelected: boolean) => {
    setSelectedIds((prev) =>
      isSelected ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((x) => x !== id)
    );
  }, []);

  const handleSoTienChange = useCallback((id: number, value: number) => {
    setDatasource((prev) =>
      prev.map((x) => (x.id === id && x.so_tien_phai_nop !== value ? { ...x, so_tien_phai_nop: value } : x))
    );
  }, []);

  const handleAdd = async () => {
    const selected = datasource.filter((x) => selectedIds.includes(x.id));
    if (props.isSaveToSaleset === false) {
      props.onAdd(selected);
      props.onClose();
      return;
    }
    setIsSaving(true);
    try {
      const res = await saleSetApi.addMultiple({
        nam_hoc: props.nam_hoc,
        ts_hocsinh_id: props.ts_hocsinh_id,
        items: selected.map((x) => ({
          sf_khoannop_id: x.sf_khoannop_id ?? x.id,
          so_tien_dinh_muc: x.so_tien_phai_nop || x.so_tien || 0,
          so_tien_mien_giam: x.so_tien_mien_giam || 0,
          ghi_chu: x.ghi_chu || "",
          isShowPayment: true,
        })),
      });
      if (res.is_success) {
        showNotify({ message: "Thêm khoản nộp thành công", type: "success" });
        props.onAdd(selected);
        props.onClose();
      } else {
        showNotify({ message: res.message || "Có lỗi, vui lòng kiểm tra lại", type: "error" });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddAndSubmit = async () => {
    const selected = datasource.filter((x) => selectedIds.includes(x.id));
    setIsSaving(true);
    try {
      const res = await saleSetApi.addMultiple({
        nam_hoc: props.nam_hoc,
        ts_hocsinh_id: props.ts_hocsinh_id,
        items: selected.map((x) => ({
          sf_khoannop_id: x.sf_khoannop_id ?? x.id,
          so_tien_dinh_muc: x.so_tien_phai_nop || x.so_tien || 0,
          so_tien_mien_giam: x.so_tien_mien_giam || 0,
          ghi_chu: x.ghi_chu || "",
          isShowPayment: true,
        })),
      });
      if (res.is_success) {
        showNotify({ message: "Thêm khoản nộp thành công", type: "success" });
        props.onAddAndSubmit?.(selected);
        props.onClose();
      } else {
        showNotify({ message: res.message || "Có lỗi, vui lòng kiểm tra lại", type: "error" });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const columns: IDataGridColumn[] = useMemo(() => [
    {
      caption: "Tên khoản nộp",
      id: "ten_khoan_nop",
      dataField: "ten_khoan_nop",
      width: 280,
      align: "left",
      cellRender: (data: any) => {
        const isExpanded = expandedGroups.has(data.nhom_khoan_nop || "Khác");
        const level = data.isGroupHeader ? 0 : 1;
        if (data.isGroupHeader) {
          const groupKey = data.nhom_khoan_nop || "Khác";
          const groupIds = datasource.filter((x) => (x.nhom_khoan_nop || "Khác") === groupKey).map((x) => x.id);
          const isAll = groupIds.length > 0 && groupIds.every((id) => selectedIds.includes(id));
          const isPartial = groupIds.some((id) => selectedIds.includes(id));
          return (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ width: `${level * 30}px` }}>&nbsp;</Box>
              <IconButton icon={!isExpanded ? ChevronRightIcon : ChevronUpIcon} aria-label="" variant="invisible" size="small" onClick={() => toggleGroup(groupKey)} />
              <Checkbox checked={isAll} indeterminate={isPartial && !isAll} onChange={(e) => handleGroupSelection(groupKey, e.target.checked)} />
              <Box sx={{ fontSize: "13px", fontWeight: "bold", marginLeft: "8px", color: "#0969da" }}>
                Nhóm: {groupKey}
              </Box>
            </Box>
          );
        }
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ width: `${level * 30}px` }}>&nbsp;</Box>
            <Checkbox checked={selectedIds.includes(data.id)} onChange={(e) => handleItemSelection(data.id, e.target.checked)} />
            <Box sx={{ marginLeft: "8px" }}><TextWithEllipse text={data.ten_khoan_nop || ""} lineNumber={2} /></Box>
          </Box>
        );
      },
    },
    {
      caption: "Mã khoản nộp",
      id: "ma_khoan_nop",
      dataField: "ma_khoan_nop",
      width: 130,
      align: "left",
      cellRender: (data: any) => data.isGroupHeader ? <span /> : <TextWithEllipse text={data.ma_khoan_nop || ""} lineNumber={1} />,
    },
    {
      caption: "Số tiền định mức",
      id: "so_tien_phai_nop",
      dataField: "so_tien_phai_nop",
      width: 140,
      align: "right",
      isAllowFocus: true,
      cellRender: (data: any) => {
        if (data.isGroupHeader) return <span />;
        const value = data.so_tien_phai_nop || 0;
        return (
          <Box sx={{ width: "100%", "& .dx-texteditor-input": { textAlign: "right" } }}>
            <NumberBox
              value={value}
              format="#,##0"
              width="100%"
              stylingMode="outlined"
              onValueChanged={(e) => {
                if (e.value !== null && e.value !== undefined && e.value !== value) {
                  handleSoTienChange(data.id, e.value);
                }
              }}
            />
          </Box>
        );
      },
    },
  ], [expandedGroups, selectedIds, datasource, toggleGroup, handleGroupSelection, handleItemSelection, handleSoTienChange]);

  return (
    <DefaultPopup width="80%" title="Thêm Hè & CLB" onClose={props.onClose}>
      <DataGrid
        dataSource={data_source}
        columns={columns as any}
        height={window.innerHeight - 300}
        isLoading={isLoading}
        paging={{ enable: false }}
        filterRow={{ enable: true }}
      />
      <ModalActions>
        <Button variant="default" leadingVisual={XIcon} text="Đóng" onClick={props.onClose} />
        <Button
          variant="primary"
          leadingVisual={CheckIcon}
          text={`Thêm${selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}`}
          disabled={selectedIds.length === 0}
          isLoading={isSaving}
          onClick={handleAdd}
        />
        {props.isSaveToSaleset !== false && props.onAddAndSubmit && (
          <Button
            variant="primary"
            leadingVisual={CheckIcon}
            text={`Thêm và tạo chứng từ${selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}`}
            disabled={selectedIds.length === 0}
            isLoading={isSaving}
            onClick={handleAddAndSubmit}
          />
        )}
      </ModalActions>
    </DefaultPopup>
  );
};
